<!--
  The standing invitation to turn notifications on.

  Permanently visible wherever the parent mounts it, for as long as push is
  supported and OFF — deliberately no "not now" and no snooze: a user who
  dismissed a prompt half-consciously would never find the feature again,
  while a quiet one-line bar costs nothing and is always where they look.
  When the system permission was denied, the bar says where to unblock it
  instead of offering a button that cannot work.
-->
<script lang="ts">
  import { t } from '$lib/i18n/index.svelte';
  import {
    currentSubscription,
    enablePush,
    pushSupported,
    renewCapabilitiesWith,
    type EnableFailure,
  } from '$lib/push/push';

  import Banner from './Banner.svelte';

  type State = 'hidden' | 'off' | 'denied' | 'busy' | 'error';
  let stage = $state<State>('hidden');
  let failure = $state<EnableFailure | null>(null);
  let failureDetail = $state<string | undefined>(undefined);

  async function evaluate(): Promise<void> {
    if (!pushSupported()) {
      stage = 'hidden';
      return;
    }
    if (Notification.permission === 'denied') {
      stage = 'denied';
      return;
    }
    stage = (await currentSubscription()) !== null ? 'hidden' : 'off';
  }

  $effect(() => {
    void evaluate();
  });

  async function turnOn(): Promise<void> {
    stage = 'busy';
    const result = await enablePush();
    if (result.ok) {
      await renewCapabilitiesWith(result.subscription);
      stage = 'hidden';
      return;
    }
    failure = result.reason;
    failureDetail = result.detail;
    stage = result.reason === 'permission_denied' ? 'denied' : 'error';
  }

  const failureText = (reason: EnableFailure): string => {
    switch (reason) {
      case 'permission_dismissed':
        return t('push.fail.dismissed');
      case 'no_gateway_key':
        return t('push.fail.noGatewayKey');
      case 'no_service_worker':
        return t('push.fail.notReady');
      case 'subscribe_failed':
        // The engine's own error rides along — one line that turns a report
        // from a phone into a diagnosis instead of a guess.
        return failureDetail === undefined
          ? t('push.fail.subscribe')
          : `${t('push.fail.subscribe')} (${failureDetail})`;
      case 'permission_denied':
        return t('settings.push.denied');
    }
  };
</script>

{#if stage === 'off' || stage === 'busy'}
  <Banner
    kind="info"
    text={t('push.nudge.body')}
    actionLabel={stage === 'busy' ? '…' : t('push.nudge.enable')}
    onaction={() => void turnOn()}
  />
{:else if stage === 'error' && failure !== null}
  <Banner
    kind="warn"
    text={failureText(failure)}
    actionLabel={t('push.nudge.retry')}
    onaction={() => void turnOn()}
  />
{:else if stage === 'denied'}
  <Banner kind="info" text={t('settings.push.denied')} />
{/if}
